// ENT-2439: the leading-label strip accepts the common www typos too
// (ww. / wwww.) — production listings with a typo'd ww. label terminalized
// as dead because the label has no DNS delegation. Single-w and real
// subdomains (w.wiki, smb.example.com) are preserved.
export function normalizeDomainInput(value: string | null | undefined): string | null {
  if (!value) return null;

  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;

  // Treat only a bare email-shaped value as email. URL userinfo, paths and
  // query strings may legitimately contain @ and must retain the parsed host.
  const emailDomain = /^[^/?#@\s]+@[^/?#@\s]+$/.test(trimmed)
    ? trimmed.split('@').at(-1)?.trim() ?? ''
    : trimmed;
  if (!emailDomain) return null;

  const schemeStripped = emailDomain
    .replace(/^([a-z][a-z0-9+.-]*:\/\/)+/, '')
    .trim();
  if (!schemeStripped) return null;

  const candidate = `https://${schemeStripped}`;

  try {
    const parsed = new URL(candidate);
    const hostname = parsed.hostname
      .trim()
      .toLowerCase()
      .replace(/^w{2,4}\./, '')
      .replace(/\.$/, '');
    return hostname && hostname.includes('.') ? hostname : null;
  } catch {
    // The URL parser is the canonical WHATWG/UTS-46 boundary. Returning a
    // hand-stripped Unicode hostname here would re-admit disallowed or
    // unassigned code points that the parser intentionally rejected.
    return null;
  }
}

export function normalizeWebsiteUrlInput(value: string | null | undefined): string | null {
  const domain = normalizeDomainInput(value);
  return domain ? `https://${domain}` : null;
}

export function hasCanonicalDomainChanged(
  nextDomain: string | null | undefined,
  currentDomain: string | null | undefined,
): boolean {
  return normalizeDomainInput(nextDomain) !== normalizeDomainInput(currentDomain);
}
